import React, { useState, useRef, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import * as yup from 'yup'
import { Formik, Form, FormikProps } from 'formik'
import { HandleMassSubmissionFunction } from '../types'
import { selectEditTagGroupOptions, selectRejectionTagGroupOptions } from 'state/constants'
import { selectSidebarStep, REVIEW_MODAL_TYPE } from 'state/review'
import { STEP_TYPE_ENUM } from 'services/api/constants'
import _ from 'lodash'
import Modal from 'particles/Modal'
import Dropdown from 'particles/Dropdown'
import MenuItem from 'particles/MenuItem'
import { FormikTextArea } from 'particles/TextInputs/TextArea'
import styled from 'particles/themes/styled'
interface FormValues {
    comment?: string
    tag_uuids: string[]
}
const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[400]};
`

interface MassRejectAndEditModalProps {
    selectedNum: number
    handleAction?: HandleMassSubmissionFunction
    setOpen: (value: boolean) => void
    type: REVIEW_MODAL_TYPE
}

const MassRejectAndEditModal: React.FC<MassRejectAndEditModalProps> = ({
    selectedNum,
    handleAction,
    setOpen,
    type,
}) => {
    const [t] = useTranslation()
    const sidebarStep = useSelector(selectSidebarStep)
    const [search, setSearch] = useState<string | null>(null)
    const [values, setValues] = useState<string[]>()
    const editTagGroupOptions = useSelector(selectEditTagGroupOptions())
    const rejectionTagGroupOptions = useSelector(selectRejectionTagGroupOptions())
    const typeOptions = type === REVIEW_MODAL_TYPE.EDIT_REQUEST ? editTagGroupOptions : rejectionTagGroupOptions
    const formikRef = useRef<FormikProps<FormValues>>(null)

    const validation = yup.object({
        comment: yup
            .string()
            .max(10000, ({ max }) =>
                t('errorMessage.validation.enterLessThanCharacterLimit', 'Please enter less than {{max}} characters.', {
                    max,
                }),
            )
            .nullable()
            .required(t('errorMessage.validation.enterRequestEditReason')),
        tag_uuids: yup.array().required(t('errorMessage.validation.selectRequestEditReason')),
    })

    const options = useMemo(() => {
        if (search !== null) {
            const searchOptions = [...typeOptions].map(option => {
                return {
                    ...option,
                    options: option.options.filter(item => item.label.toLowerCase().includes(search)),
                }
            })
            return searchOptions
        } else {
            return typeOptions
        }
    }, [typeOptions, search, type])

    const prefillComments = useMemo(() => {
        const comments: Record<string, string> = {}
        if (typeOptions) {
            typeOptions.forEach(option => {
                if (option?.options.length > 0) {
                    option.options.forEach(item => {
                        comments[item.uuid] = item.value
                    })
                }
            })
        }

        return comments
    }, [typeOptions])

    const displayOptions = useMemo(() => {
        const returnOptions: { label: string; value: string; isLabel: boolean }[] = []
        options.forEach(option => {
            returnOptions.push({
                label: option.label,
                value: '',
                isLabel: true,
            })
            if (option.options.length === 0) {
                returnOptions.push({
                    label: t('placeholderText.noSearchResult'),
                    value: '',
                    isLabel: false,
                })
            } else {
                option.options.forEach(item => {
                    returnOptions.push({
                        label: item.label,
                        value: item.uuid,
                        isLabel: false,
                    })
                })
            }
        })
        return returnOptions
    }, [options])

    const onSearch = (value: string) => {
        setSearch(value)
    }
    const onClearSearch = () => {
        setSearch(null)
    }

    const handleConfirm = () => {
        if (formikRef.current) {
            formikRef.current.submitForm()
        }
    }

    useEffect(() => {
        let comment = ''
        if (values && values?.length > 0) {
            values.forEach(value => {
                if (prefillComments[value]) {
                    comment = comment + ' - ' + prefillComments[value] + '\n'
                }
            })
        }
        if (formikRef.current) {
            formikRef.current.setFieldValue('comment', comment)
        }
    }, [prefillComments, values])

    return (
        <Modal
            title={
                type === REVIEW_MODAL_TYPE.EDIT_REQUEST
                    ? t('modalWindow.confirmationMessage.requestEditTitle', {
                          selectedNum,
                      })
                    : t('modalWindow.confirmationMessage.rejectPartiposterTitle', {
                          selectedNum,
                      })
            }
            subtext={
                sidebarStep &&
                [
                    STEP_TYPE_ENUM.SUBMIT_PROOF_IG_SCREENSHOT,
                    STEP_TYPE_ENUM.SUBMIT_ENGAGEMENT_RESULTS_AND_PROOF_IG_SCREENSHOT,
                ].includes(sidebarStep.type)
                    ? type === REVIEW_MODAL_TYPE.EDIT_REQUEST
                        ? t('modalWindow.confirmationMessage.turnOffMultiSelectSubtitle')
                        : t('modalWindow.confirmationMessage.turnOffMultiSelectSubtitle')
                    : type === REVIEW_MODAL_TYPE.EDIT_REQUEST
                      ? t('modalWindow.confirmationMessage.requestEditFromPartipostersBulkSubtitle', {
                            count: selectedNum,
                        })
                      : t('modalWindow.confirmationMessage.rejectPartipostersBulkSubtitle', {
                            count: selectedNum,
                        })
            }
            hasIcon={false}
            size="m"
            primaryButtonProps={{
                label: type === REVIEW_MODAL_TYPE.EDIT_REQUEST ? t('button.requestEditsForAll') : t('button.rejectAll'),
                isDisabled: !values || values.length === 0,
            }}
            secondaryButtonProps={{
                label: t('button.cancel'),
            }}
            onConfirm={() => {
                handleConfirm()
            }}
            onClose={() => setOpen(false)}
        >
            <Formik
                innerRef={formikRef}
                initialValues={{ comment: '', tag_uuids: [] }}
                enableReinitialize={true}
                validationSchema={validation}
                onSubmit={values => {
                    if (handleAction) {
                        handleAction({ comment: values.comment, tag_uuids: values.tag_uuids })
                    }
                }}
            >
                {(formikBag: FormikProps<FormValues>) => (
                    <Form>
                        <Container>
                            <Dropdown
                                label={
                                    type === REVIEW_MODAL_TYPE.EDIT_REQUEST
                                        ? t('submission.review.reasonsForRequestEditTitle')
                                        : t('submission.review.reasonsForRejectionTitle')
                                }
                                multiple
                                value={formikBag.values.tag_uuids}
                                onChange={(value?: unknown) => {
                                    formikBag.setFieldValue('tag_uuids', value)
                                    setValues(value as string[])
                                }}
                                onClose={onClearSearch}
                            >
                                <MenuItem.Search onSearch={onSearch} onClearSearch={onClearSearch} />
                                {displayOptions.map((option, index) =>
                                    option.isLabel ? (
                                        <MenuItem.Title text={option.label} />
                                    ) : (
                                        <MenuItem.Checkbox
                                            key={`menu-item-${index}`}
                                            text={option.label}
                                            tabIndex={index}
                                            value={option.value}
                                        />
                                    ),
                                )}
                            </Dropdown>
                            <FormikTextArea
                                name="comment"
                                placeholder={t('submission.review.reasonsForRequestEditSubtitle')}
                                minTextAreaHeight={100}
                                maxTextAreaHeight={200}
                                isDisabled={!values || values.length === 0}
                            />
                        </Container>
                    </Form>
                )}
            </Formik>
        </Modal>
    )
}

export default MassRejectAndEditModal
